import { useState, useEffect, useRef } from 'react';
import { fetchAdminActivity } from '@/api/admin';
import type { AdminActivityEntry, AdminActivityParams } from '@/api/admin';
import { useAuthStore } from '@/stores/auth';
import {
  DUTCH_DAYS,
  DUTCH_MONTHS,
  EMPTY_LOG_FILTERS,
  hasActiveFilters,
  matchesStatusFilter,
  formatLogTime,
  type LogFilters,
} from './adminLog/logFormat';
import { LogFilterBar } from './adminLog/LogFilterBar';
import { LogEntryRow } from './adminLog/LogRows';

export function AdminActivityLogView() {
  const [entries, setEntries] = useState<AdminActivityEntry[]>([]);
  const [connected, setConnected] = useState(false);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<LogFilters>(EMPTY_LOG_FILTERS);
  const [total, setTotal] = useState({ requests: 0, actions: 0, errors: 0 });
  const [knownUsers, setKnownUsers] = useState<string[]>([]);
  const pausedRef = useRef(paused);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Fetch entries when filters change (server-side filtering)
  useEffect(() => {
    setLoading(true);
    setError(null);
    const params: AdminActivityParams = { limit: 2000 };
    if (filters.method) params.method = filters.method;
    if (filters.user) params.user = filters.user;
    if (filters.type !== 'all') params.type = filters.type;
    // Exact status code wins over the broader class (matches the API).
    if (filters.status) params.status = Number(filters.status);
    else if (filters.statusClass) params.statusClass = filters.statusClass;
    if (filters.from) params.from = new Date(filters.from + 'T00:00:00').toISOString();
    if (filters.to) params.to = new Date(filters.to + 'T23:59:59').toISOString();

    fetchAdminActivity(params)
      .then(data => {
        setEntries(data.entries);
        setTotal({ requests: data.requestCount, actions: data.actionCount, errors: data.errorCount });
        if (!hasActiveFilters(filters)) {
          setKnownUsers([...new Set(data.entries.map(e => e.userName))].sort());
        }
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Onbekende fout'))
      .finally(() => setLoading(false));
  }, [filters.method, filters.user, filters.type, filters.statusClass, filters.status, filters.from, filters.to]);

  // SSE connection for real-time streaming
  // Reconnects with a fresh token after consecutive errors (handles token expiry)
  useEffect(() => {
    let es: EventSource | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let errorCount = 0;
    let closed = false;

    function connect() {
      if (closed) return;

      const token = useAuthStore.getState().token;
      if (!token) {
        // No token available, retry in 5s
        reconnectTimeout = setTimeout(connect, 5000);
        return;
      }

      const baseUrl = import.meta.env.VITE_API_URL || '';
      es = new EventSource(`${baseUrl}/api/admin/activity/stream?token=${token}`);

      es.onopen = () => {
        setConnected(true);
        errorCount = 0; // Reset on successful connection
      };

      es.onmessage = (event) => {
        if (pausedRef.current) return;
        try {
          const entry = JSON.parse(event.data) as AdminActivityEntry;
          const f = filtersRef.current;

          const matchesType = f.type === 'all' ||
            (f.type === 'requests' && entry.type === 'request') ||
            (f.type === 'actions' && entry.type === 'action') ||
            (f.type === 'errors' && entry.type === 'error');
          const matchesMethod = !f.method || entry.method === f.method;
          const matchesUser = !f.user || entry.userName === f.user;
          const matchesStatus = matchesStatusFilter(entry.status, f.statusClass, f.status);

          if (matchesType && matchesMethod && matchesUser && matchesStatus) {
            setEntries(prev => {
              const next = [entry, ...prev];
              return next.length > 2000 ? next.slice(0, 2000) : next;
            });
          }

          setTotal(prev => ({
            requests: entry.type === 'request' ? prev.requests + 1 : prev.requests,
            actions: entry.type === 'action' ? prev.actions + 1 : prev.actions,
            errors: entry.type === 'error' ? prev.errors + 1 : prev.errors,
          }));

          setKnownUsers(prev => {
            if (prev.includes(entry.userName)) return prev;
            return [...prev, entry.userName].sort();
          });
        } catch { /* ignore malformed SSE data */ }
      };

      es.onerror = () => {
        setConnected(false);
        errorCount++;

        // After 3 consecutive errors, close and reconnect with fresh token
        // This handles token expiry — EventSource can't update its URL
        if (errorCount >= 3 && es) {
          es.close();
          es = null;
          // Wait 3 seconds then reconnect with fresh token
          reconnectTimeout = setTimeout(connect, 3000);
        }
      };
    }

    connect();

    return () => {
      closed = true;
      if (es) es.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, []);

  // Group by date
  const grouped = new Map<string, AdminActivityEntry[]>();
  for (const entry of entries) {
    const key = new Date(entry.ts).toLocaleDateString('nl-BE');
    const existing = grouped.get(key) ?? [];
    existing.push(entry);
    grouped.set(key, existing);
  }

  return (
    <div className="px-4 lg:px-8 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div>
          <h1 className="text-xl font-bold text-td-navy">Server Console</h1>
          <p className="text-sm text-td-muted">
            {total.requests} requests, {total.actions} acties
            {total.errors > 0 && <span className="text-red-600">, {total.errors} fouten</span>}
            {(total.requests + total.actions + total.errors) > 2000 && (
              <span className="text-amber-600 ml-1">(max 2000 getoond)</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
            <span className="text-xs text-td-muted">{connected ? 'Verbonden' : 'Verbroken'}</span>
          </div>
          <button
            onClick={() => { setPaused(!paused); pausedRef.current = !paused; }}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${
              paused ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-white border-gray-200 text-td-muted hover:bg-gray-50'
            }`}
          >
            {paused ? 'Hervatten' : 'Pauze'}
          </button>
        </div>
      </div>

      <LogFilterBar filters={filters} setFilters={setFilters} knownUsers={knownUsers} />

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12 text-td-muted text-sm">Laden...</div>
      )}

      {!loading && error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Kon activiteitenlog niet laden: {error}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && entries.length === 0 && (
        <div className="text-center py-16">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-td-muted text-sm font-medium">Geen activiteiten gevonden</p>
        </div>
      )}

      {/* Grouped entries */}
      {!error && [...grouped.entries()].map(([dateStr, dayEntries]) => {
        const firstDate = new Date(dayEntries[0].ts);
        const label = `${DUTCH_DAYS[firstDate.getDay()]} ${firstDate.getDate()} ${DUTCH_MONTHS[firstDate.getMonth()]}`;

        return (
          <div key={dateStr} className="mb-6">
            <h2 className="text-xs font-bold text-td-muted uppercase tracking-wider mb-2">{label}</h2>
            <div className="bg-white border rounded-lg divide-y divide-gray-100">
              {dayEntries.map((entry, i) => (
                <LogEntryRow
                  key={entry.ts + (entry.path || entry.msg || entry.action || '') + i}
                  entry={entry}
                  time={formatLogTime(entry.ts)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
